import { Controller, DefaultValues, useForm } from "react-hook-form"
import { PaymentFormValues, usePaymentFormSchema, usePaymentMethodSelectOptions } from "./paymentForm.utils"
import { zodResolver } from "@hookform/resolvers/zod"
import { Product } from "../../acquisitionForm/form/useAcquisitionFormSchema"
import { Subheader } from "../components/Subheader"
import { Spacer } from "../../../../../components/spacer/Spacer"
import { theme } from "../../../../../styles/stitches.config"
import { FormItem, FormLabel } from "../components/Form"
import { FormSelect } from "../components/Select"
import { getFormError, getFormSuccess } from "../../../../../utils/form"
import { BankAccountInputBlock } from "./BankAccountInputBlock"
import { CheckboxedSection } from "../components/Checkbox"
import { trpc } from "../../../../../api/trpc/trpc"
import Loader from "../../../../../components/loader/Loader"
import { FormTextArea } from "../components/TextArea"
import { NavigationButtons } from "../components/NavigationButtons"
import { useMemo } from "react"
import { Flex } from "../../../../../components/flex/Flex"
import { InputDisclaimer } from "../components/Input"

type Props = {
  selectedProduct: Product
  defaultValues?: DefaultValues<PaymentFormValues>
  onSubmit?: (formValues: PaymentFormValues) => void
  onReturn?: () => void
}

export function PaymentForm(props: Props) {
  const partnerQuery = trpc.useQuery(["web.getPartnerList"])
  const schema = usePaymentFormSchema(props.selectedProduct)
  const paymentMethodOptions = usePaymentMethodSelectOptions()
  const form = useForm<PaymentFormValues>({
    resolver: zodResolver(schema),
    mode: "onBlur",
    defaultValues: {
      advancePaymentMethod: "BANK_ENCASHMENT",
      advancePaymentBankAccount: {
        prefix: "",
        bankCode: "",
        number: "",
      },
      partnerCode: "",
      message: "",
      ...props.defaultValues,
    },
  })

  const productionPaymentAccountWatch = form.watch("productionPaymentBankAccount")

  function handleSubmit(formValues: PaymentFormValues) {
    props.onSubmit?.(formValues)
  }

  const partnerOptions = useMemo(() => {
    const partnerOptions: Record<string, string> = {}
    if (!partnerQuery.data) {
      return partnerOptions
    }

    for (const partner of partnerQuery.data) {
      partnerOptions[partner.code] = partner.name
    }

    return partnerOptions
  }, [partnerQuery.data])

  if (partnerQuery.isLoading || partnerQuery.error || !partnerQuery.data) {
    return (
      <Flex align="center" justify="center">
        <Loader />
      </Flex>
    )
  }

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <Subheader asSectionHeader>
        {props.selectedProduct === "production" ? "Účet pro úhradu výroby" : "Způsob platby"}
      </Subheader>
      <Spacer size={theme.space.s6} />

      {(props.selectedProduct === "consumption" || props.selectedProduct === "productionAndConsumption") && (
        <>
          <Controller
            control={form.control}
            name="advancePaymentMethod"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Forma placení záloh</FormLabel>
                <FormSelect
                  id="payment-type"
                  options={paymentMethodOptions}
                  placeholder="Vyberte formu placení záloh"
                  {...field}
                  error={getFormError({ form, name: "advancePaymentMethod" })}
                  success={getFormSuccess({ form, name: "advancePaymentMethod" })}
                />
              </FormItem>
            )}
          />

          <Spacer size={theme.space.s3} />
          <InputDisclaimer
            label={
              <>
                U průběhového měření dochází k měsíčnímu vyúčtování a{" "}
                <strong>
                  zálohy se mohou měnit podle Vaší aktuální spotřeby. Inkaso zajistí, že všechny platby budou provedeny
                  vždy včas a Vy se nemusíte o nic dalšího starat.
                </strong>{" "}
                Pokud potřebujete pomoci s nastavením inkasa, obraťte se prosím na naší zákaznickou linku, rádi Vám
                poradíme, jak inkaso nastavit.
              </>
            }
          />

          <Spacer size={theme.space.s9} />

          <BankAccountInputBlock
            form={form}
            prefixFieldName="advancePaymentBankAccount.prefix"
            numberFieldName="advancePaymentBankAccount.number"
            bankCodeFieldName="advancePaymentBankAccount.bankCode"
          />

          <Spacer size={theme.space.s3} />
          <InputDisclaimer label="Zadané číslo účtu použijeme i pro vrácení přeplatku." />

          {props.selectedProduct === "productionAndConsumption" && (
            <>
              <Spacer size={theme.space.s9} />

              <CheckboxedSection
                open={!!productionPaymentAccountWatch}
                onCollapse={() => {
                  form.resetField("productionPaymentBankAccount")
                  // eslint-disable-next-line unicorn/no-useless-undefined
                  form.setValue("productionPaymentBankAccount", undefined)
                }}
                checkboxLabel="Platby za výrobu chci zasílat na jiný účet"
                hiddenContent={
                  <BankAccountInputBlock
                    form={form}
                    prefixFieldName="productionPaymentBankAccount.prefix"
                    numberFieldName="productionPaymentBankAccount.number"
                    bankCodeFieldName="productionPaymentBankAccount.bankCode"
                  />
                }
              />
            </>
          )}

          <Spacer size={theme.space.s9} />
        </>
      )}

      {props.selectedProduct === "production" && (
        <BankAccountInputBlock
          form={form}
          prefixFieldName="productionPaymentBankAccount.prefix"
          numberFieldName="productionPaymentBankAccount.number"
          bankCodeFieldName="productionPaymentBankAccount.bankCode"
        />
      )}

      <Spacer size={theme.space.s9} />

      <Controller
        control={form.control}
        name="partnerCode"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Doporučila vám Delta Green některá z níže uvedených firem?</FormLabel>
            <FormSelect
              id="partner"
              options={partnerOptions}
              placeholder="Vyberte"
              {...field}
              error={getFormError({ form, name: "partnerCode" })}
              success={getFormSuccess({ form, name: "partnerCode" })}
            />
          </FormItem>
        )}
      />

      <Spacer size={theme.space.s11} />

      <Controller
        control={form.control}
        name="message"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Chcete nám něco vzkázat?</FormLabel>
            <FormTextArea placeholder="Napište nám..." {...field} />
          </FormItem>
        )}
      />

      <Spacer size={theme.space.s15} />

      <NavigationButtons
        prevText={
          props.selectedProduct === "productionAndConsumption" || props.selectedProduct === "production"
            ? "Výrobní místo"
            : "Odběrné místo"
        }
        nextText="Souhrn"
        onReturn={props.onReturn}
        isSubmitting={form.formState.isSubmitting}
      />
    </form>
  )
}
